import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { onAuthStateChanged, User } from "firebase/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { auth } from "src/utils/firebase";
import { apiClient } from "src/lib/apiClient";
import { keys, staleTimes } from "src/queries/keys";
import { mapLinkedLocation } from "src/queries/mappers";

/**
 * The signed-in user.
 *
 * Deliberately the same shape the `user` slice exposed, so the call sites that
 * read it did not have to change when it moved out of Redux.
 */
export interface AuthUser {
  id: number;
  uid: string;
  displayName: string | null;
  emailVerified: boolean;
  email: string;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
  /** True once Firebase has a user AND /auth/me has come back. */
  loggedIn: boolean;
  /** A sign-in is in progress: Firebase has a user, /auth/me has not answered. */
  loading: boolean;
  /** The auth listener has settled — signed in or definitively signed out. */
  bootstrapped: boolean;
}

const EMPTY_USER: AuthUser = {
  id: -1,
  uid: "",
  displayName: null,
  emailVerified: false,
  email: "",
  firstName: "",
  lastName: "",
  isSuperAdmin: false,
  loggedIn: false,
  loading: false,
  bootstrapped: false,
};

const AuthContext = createContext<AuthUser>(EMPTY_USER);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const currentUser = useRef<User | null>(null);
  const tokenRefreshInterval = useRef<NodeJS.Timeout | null>(null);

  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  /**
   * The token lives in state, not only on the axios default header.
   *
   * That header is a mutable global: reading it as a readiness signal does not
   * re-render, which is why the old code had it sitting in a dependency array
   * where it could never fire. Holding it here is what makes `enabled` below
   * work at all.
   */
  const [token, setToken] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  /** The auth listener has fired at least once. */
  const [ready, setReady] = useState(false);

  const refreshAuthToken = async () => {
    try {
      if (currentUser.current) {
        const idTokenResult = await currentUser.current.getIdTokenResult(true);
        axios.defaults.headers.common.Authorization = idTokenResult.token;
        setToken(idTokenResult.token);
        return idTokenResult.token;
      }
    } catch (error) {
      console.error("Error refreshing auth token:", error);
    }
    return null;
  };

  // Interceptors stay on the global axios default as well as the shared client,
  // because both are still in use during the migration.
  useEffect(() => {
    const attach = (instance: typeof axios | typeof apiClient) => {
      const request = instance.interceptors.request.use(
        async (config) => {
          if (currentUser.current) {
            try {
              const idTokenResult = await currentUser.current.getIdTokenResult(false);
              const expirationTime = new Date(idTokenResult.expirationTime).getTime();
              const fiveMinutesInMs = 5 * 60 * 1000;

              if (expirationTime - Date.now() < fiveMinutesInMs) {
                const newToken = await refreshAuthToken();
                if (newToken && config.headers) {
                  config.headers.Authorization = newToken;
                }
              } else if (config.headers && !config.headers.Authorization) {
                config.headers.Authorization = idTokenResult.token;
              }
            } catch (error) {
              console.error("Error checking token expiration:", error);
            }
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      const response = instance.interceptors.response.use(
        (res) => res,
        async (error) => {
          const originalRequest = error.config;
          if (
            error.response?.status === 401 &&
            !originalRequest?._retry &&
            currentUser.current
          ) {
            originalRequest._retry = true;
            try {
              const newToken = await refreshAuthToken();
              if (newToken) {
                originalRequest.headers.Authorization = newToken;
                return instance(originalRequest);
              }
            } catch (refreshError) {
              console.error("Error refreshing token on 401:", refreshError);
            }
          }
          return Promise.reject(error);
        }
      );

      return () => {
        instance.interceptors.request.eject(request);
        instance.interceptors.response.eject(response);
      };
    };

    const detachAxios = attach(axios);
    const detachClient = attach(apiClient);
    return () => {
      detachAxios();
      detachClient();
    };
  }, []);

  useEffect(() => {
    const setupTokenRefresh = () => {
      if (tokenRefreshInterval.current) {
        clearInterval(tokenRefreshInterval.current);
      }
      // Tokens last an hour; refresh at 50 minutes.
      tokenRefreshInterval.current = setInterval(refreshAuthToken, 50 * 60 * 1000);
    };

    const unsubscribe = onAuthStateChanged(auth, async (fbUser: User | null) => {
      currentUser.current = fbUser;

      if (fbUser) {
        try {
          const idTokenResult = await fbUser.getIdTokenResult(true);
          axios.defaults.headers.common.Authorization = idTokenResult.token;
          setToken(idTokenResult.token);
          setIsSuperAdmin(!!idTokenResult.claims?.isSuperAdmin);
          setFirebaseUser(fbUser);
          setupTokenRefresh();
        } catch (error) {
          console.error("Error reading auth token:", error);
          setFirebaseUser(null);
          setToken(null);
        }
      } else {
        axios.defaults.headers.common.Authorization = null;
        setToken(null);
        setIsSuperAdmin(false);
        setFirebaseUser(null);

        if (tokenRefreshInterval.current) {
          clearInterval(tokenRefreshInterval.current);
          tokenRefreshInterval.current = null;
        }

        // Order matters. The header is already cleared, so nothing new can be
        // fetched as the previous user; cancelling in-flight requests before
        // emptying the cache stops a late response from repopulating it.
        await queryClient.cancelQueries();
        queryClient.clear();
      }

      setReady(true);
    });

    return () => {
      unsubscribe();
      if (tokenRefreshInterval.current) {
        clearInterval(tokenRefreshInterval.current);
      }
    };
  }, [queryClient]);

  /** GET auth/me — identity, refetched only when the token changes. */
  const me = useQuery({
    queryKey: keys.auth.me(),
    enabled: !!token,
    staleTime: staleTimes.session,
    // No retries: the app-level gate below waits on this query, so retrying
    // would hold every user behind the loader for three round trips before a
    // failure could resolve it. The old code made a single attempt and released
    // the gate in its catch; this keeps that timing.
    retry: false,
    queryFn: async () => {
      const response = await apiClient.get("auth/me");
      return response.data.data;
    },
  });

  // Start the locations list before the gate below releases, so it is already
  // in flight when the first route renders and the page's own spinner takes
  // over from the app loader. Letting /locations mount the query instead would
  // lose that head start.
  const prefetchedFor = useRef<number | null>(null);
  useEffect(() => {
    const userId = me.data?.id;
    if (!userId || prefetchedFor.current === userId) return;
    prefetchedFor.current = userId;
    queryClient.prefetchQuery({
      queryKey: keys.locations.byUser(userId),
      queryFn: async () => {
        const response = await apiClient.get("merchant/linked", {
          params: { userId },
        });
        return (response.data ?? []).map(mapLinkedLocation);
      },
    });
  }, [me.data?.id, queryClient]);

  const value = useMemo<AuthUser>(() => {
    const profile = me.data;
    return {
      id: profile?.id ?? -1,
      uid: profile?.uid ?? firebaseUser?.uid ?? "",
      displayName: profile?.displayName ?? firebaseUser?.displayName ?? null,
      emailVerified: profile?.emailVerified ?? firebaseUser?.emailVerified ?? false,
      email: profile?.email ?? firebaseUser?.email ?? "",
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      isSuperAdmin,
      loggedIn: !!firebaseUser && me.isSuccess,
      loading: !!firebaseUser && me.isPending,
      // A failed /auth/me still counts as settled: the old slice had a
      // dedicated action for exactly this so the app could not hang on a
      // loader that never resolved.
      bootstrapped: ready && (!firebaseUser || !me.isPending),
    };
  }, [me.data, me.isSuccess, me.isPending, firebaseUser, isSuperAdmin, ready]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
